import React from 'react';
import { useLanguage } from './LanguageContext';

const translations = {
  en: {
    badge: 'Superpowers',
    title: 'Not your average recipe app.',
    subtitle: 'We built Chaiffy to solve the one question nobody can answer at 6 PM.',
    features: [
      {
        icon: '🧠',
        title: 'Learns What You Like',
        description: 'The more you use Chaiffy, the smarter it gets. It remembers you hate cilantro and love extra garlic.',
        highlight: 'Personalized AI',
      },
      {
        icon: '⚡',
        title: 'Recipes in 3 Seconds',
        description: 'Not 3 minutes. Not 30 results to scroll through. One perfect recipe, instantly generated for your exact ingredients.',
        highlight: 'Lightning Fast',
      },
      {
        icon: '🕌',
        title: 'Halal & Dietary Aware',
        description: 'Halal by default. Plus full support for vegetarian, keto, gluten-free, and any allergy you throw at it.',
        highlight: 'Fully Respectful',
      },
      {
        icon: '🍚',
        title: 'Kabsa to Kunafa',
        description: 'Deep knowledge of Saudi, Levantine, Egyptian, and Gulf cuisines. From everyday rice dishes to showstopper desserts.',
        highlight: 'Arabic Cuisine Expert',
      },
      {
        icon: '💰',
        title: 'Stop Wasting Food',
        description: 'That random zucchini and the yogurt expiring tomorrow? Chaiffy turns leftovers into legitimate meals. Your wallet will thank you.',
        highlight: 'Save Money',
      },
      {
        icon: '👆',
        title: 'One Tap. That\'s It.',
        description: 'No sign-up forms. No 47-step onboarding. Open, point, snap — you\'re cooking in under a minute.',
        highlight: 'Zero Friction',
      },
    ],
    cta: 'See for Yourself',
  },
  ar: {
    badge: 'قدرات خارقة',
    title: 'مو تطبيق وصفات عادي.',
    subtitle: 'بنينا Chaiffy ليحل السؤال اللي ما أحد يقدر يجاوبه الساعة 6 المغرب.',
    features: [
      {
        icon: '🧠',
        title: 'يتعلم ذوقك',
        description: 'كل ما استخدمت Chaiffy أكثر، كل ما صار أذكى. يتذكر إنك تكره الكزبرة وتحب الثوم الزيادة.',
        highlight: 'ذكاء اصطناعي شخصي',
      },
      {
        icon: '⚡',
        title: 'وصفات في 3 ثوانٍ',
        description: 'مو 3 دقائق. ومو 30 نتيجة تتصفحها. وصفة واحدة مثالية، تُنشأ فوراً لمكوناتك بالضبط.',
        highlight: 'سريع كالبرق',
      },
      {
        icon: '🕌',
        title: 'حلال وواعي غذائياً',
        description: 'حلال بشكل افتراضي. بالإضافة لدعم كامل للنباتي والكيتو وخالي الغلوتين وأي حساسية تخطر ببالك.',
        highlight: 'احترام كامل',
      },
      {
        icon: '🍚',
        title: 'من الكبسة للكنافة',
        description: 'معرفة عميقة بالمطبخ السعودي والشامي والمصري والخليجي. من أطباق الأرز اليومية لحلويات تبهر الجميع.',
        highlight: 'خبير المطبخ العربي',
      },
      {
        icon: '💰',
        title: 'وقف هدر الأكل',
        description: 'الكوسة المنسية والزبادي اللي بينتهي بكرا؟ Chaiffy يحول البقايا لوجبات حقيقية. محفظتك بتشكرك.',
        highlight: 'وفّر فلوسك',
      },
      {
        icon: '👆',
        title: 'ضغطة وحدة. بس.',
        description: 'بدون نماذج تسجيل. بدون 47 خطوة تعريفية. افتح، وجّه، صوّر — تطبخ في أقل من دقيقة.',
        highlight: 'بدون تعقيد',
      },
    ],
    cta: 'شوف بنفسك',
  }
};

const Features = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];

  return (
    <section id="features" className="section-padding bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-primary text-white rounded-full text-xs font-bold uppercase tracking-widest mb-5">
            {t.badge}
          </span>
          <h2 className="heading-lg text-dark mb-4">
            {t.title}
          </h2>
          <p className="body-lg max-w-lg mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {t.features.map((feature, index) => (
            <div 
              key={index} 
              className="bg-white rounded-2xl p-6 md:p-8 card-hover border border-gray-100 group relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-green transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              
              <div className="flex items-start gap-5">
                <div className="text-3xl md:text-4xl flex-shrink-0 mt-1">{feature.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-lg md:text-xl font-bold text-dark" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {feature.highlight}
                    </span>
                  </div>
                  <p className="text-gray-500 leading-relaxed text-sm md:text-base" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <a href="/start" className="btn-primary text-base md:text-lg">
            {t.cta} {isRTL ? '←' : '→'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Features;
